import { QuoteAmountType } from "@generated/types";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface ExchangeState {
  sellCurrency: string;
  sellAmount: number;
  buyCurrency: string;
  buyAmount: number;
  pricingRequestId?: string;
  lastEditedSide: "sell" | "buy";
  quoteType: QuoteAmountType;
}

export interface PricingState {
  exchange: ExchangeState;
  error: string | null;
  shouldPollOnSuccess: boolean;
  areCurrenciesResolved: boolean;
}

export function createInitialState(overrides?: {
  sellCurrency?: string;
  buyCurrency?: string;
  pricingRequestId?: string;
}): PricingState {
  return {
    exchange: {
      sellAmount: 0,
      sellCurrency: overrides?.sellCurrency ?? "",
      buyCurrency: overrides?.buyCurrency ?? "",
      buyAmount: 0,
      pricingRequestId: overrides?.pricingRequestId,
      lastEditedSide: "sell",
      quoteType: QuoteAmountType.ReceiveAmount,
    },
    error: null,
    shouldPollOnSuccess: true,
    areCurrenciesResolved: false,
  };
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type PricingAction =
  | { type: "UPDATE_SELL_AMOUNT"; amount: number; isUserInput: boolean }
  | { type: "UPDATE_BUY_AMOUNT"; amount: number; isUserInput: boolean }
  | {
      type: "SET_SELL_CURRENCY";
      currency: string;
      findAlternative: (excludeCurrency: string) => string;
      hasBeneficiary: boolean;
    }
  | {
      type: "SET_BUY_CURRENCY";
      currency: string;
      findAlternative: (excludeCurrency: string) => string;
    }
  | { type: "SET_PRICING_REQUEST_ID"; id: string | undefined }
  | {
      type: "SYNC_FROM_SOURCE";
      instruction: {
        payCurrency?: string;
        receiveCurrency?: string;
        payAmount?: number;
        receiveAmount?: number;
      };
      sourceId?: string;
    }
  | {
      type: "SET_CURRENCIES";
      sellCurrency: string;
      buyCurrency: string;
      // Optional collision-resolver: in exchange flow (no beneficiary) the pair
      // must never settle on sell===buy. Mirrors SET_SELL_CURRENCY.
      findAlternative?: (excludeCurrency: string) => string;
      hasBeneficiary?: boolean;
    }
  | {
      type: "RESET";
      sellCurrency: string;
      buyCurrency: string;
    }
  | { type: "RESTORE_FROM_SESSION"; parsed: Partial<ExchangeState> }
  | { type: "SET_ERROR"; message: string }
  | { type: "CLEAR_ERROR" }
  | { type: "SET_SHOULD_POLL"; value: boolean }
  | { type: "SET_CURRENCIES_RESOLVED"; value: boolean };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function pricingReducer(
  state: PricingState,
  action: PricingAction
): PricingState {
  switch (action.type) {
    case "UPDATE_SELL_AMOUNT": {
      const prev = state.exchange;
      const nextLastEditedSide = action.isUserInput
        ? ("sell" as const)
        : prev.lastEditedSide;
      const nextQuoteType = action.isUserInput
        ? QuoteAmountType.ReceiveAmount
        : prev.quoteType;
      if (
        prev.sellAmount === action.amount &&
        prev.lastEditedSide === nextLastEditedSide &&
        prev.quoteType === nextQuoteType &&
        state.error === null
      ) {
        return state;
      }
      return {
        ...state,
        error: null,
        exchange: {
          ...prev,
          sellAmount: action.amount,
          lastEditedSide: nextLastEditedSide,
          quoteType: nextQuoteType,
        },
      };
    }

    case "UPDATE_BUY_AMOUNT": {
      const prev = state.exchange;
      const nextLastEditedSide = action.isUserInput
        ? ("buy" as const)
        : prev.lastEditedSide;
      const nextQuoteType = action.isUserInput
        ? QuoteAmountType.PayAmount
        : prev.quoteType;
      if (
        prev.buyAmount === action.amount &&
        prev.lastEditedSide === nextLastEditedSide &&
        prev.quoteType === nextQuoteType &&
        state.error === null
      ) {
        return state;
      }
      return {
        ...state,
        error: null,
        exchange: {
          ...prev,
          buyAmount: action.amount,
          lastEditedSide: nextLastEditedSide,
          quoteType: nextQuoteType,
        },
      };
    }

    case "SET_SELL_CURRENCY": {
      const { currency, findAlternative, hasBeneficiary } = action;
      const prev = state.exchange;
      if (currency === prev.buyCurrency) {
        const nextBuyCurrency = hasBeneficiary
          ? prev.buyCurrency
          : findAlternative(currency);
        if (
          prev.sellCurrency === currency &&
          prev.buyCurrency === nextBuyCurrency
        ) {
          return state;
        }
        return {
          ...state,
          exchange: {
            ...prev,
            sellCurrency: currency,
            buyCurrency: nextBuyCurrency,
          },
        };
      }
      if (prev.sellCurrency === currency) return state;
      return {
        ...state,
        exchange: { ...prev, sellCurrency: currency },
      };
    }

    case "SET_BUY_CURRENCY": {
      const { currency, findAlternative } = action;
      const prev = state.exchange;
      if (currency === prev.sellCurrency) {
        const nextSellCurrency = findAlternative(currency);
        if (
          prev.buyCurrency === currency &&
          prev.sellCurrency === nextSellCurrency
        ) {
          return state;
        }
        return {
          ...state,
          exchange: {
            ...prev,
            sellCurrency: nextSellCurrency,
            buyCurrency: currency,
          },
        };
      }
      if (prev.buyCurrency === currency) return state;
      return {
        ...state,
        exchange: { ...prev, buyCurrency: currency },
      };
    }

    case "SET_PRICING_REQUEST_ID":
      if (state.exchange.pricingRequestId === action.id) return state;
      return {
        ...state,
        exchange: { ...state.exchange, pricingRequestId: action.id },
      };

    case "SYNC_FROM_SOURCE": {
      const prev = state.exchange;
      const nextSellCurrency =
        action.instruction.payCurrency || prev.sellCurrency;
      const nextBuyCurrency =
        action.instruction.receiveCurrency || prev.buyCurrency;
      const nextSellAmount = action.instruction.payAmount || prev.sellAmount;
      const nextBuyAmount = action.instruction.receiveAmount || prev.buyAmount;
      const nextPricingRequestId = action.sourceId || prev.pricingRequestId;
      if (
        prev.sellCurrency === nextSellCurrency &&
        prev.buyCurrency === nextBuyCurrency &&
        prev.sellAmount === nextSellAmount &&
        prev.buyAmount === nextBuyAmount &&
        prev.pricingRequestId === nextPricingRequestId
      ) {
        return state;
      }
      return {
        ...state,
        exchange: {
          ...prev,
          sellCurrency: nextSellCurrency,
          buyCurrency: nextBuyCurrency,
          sellAmount: nextSellAmount,
          buyAmount: nextBuyAmount,
          pricingRequestId: nextPricingRequestId,
        },
      };
    }

    case "SET_CURRENCIES": {
      const prev = state.exchange;
      let nextBuyCurrency = action.buyCurrency;
      // Exchange flow must never settle on sell===buy (a swap to itself).
      // Payment flow MAY (same-currency payment to a same-currency
      // beneficiary), so only break the collision when there is no
      // beneficiary — mirrors SET_SELL_CURRENCY's hasBeneficiary handling. The
      // alternative is best-effort: if none exists (e.g. a single tradable
      // currency) the pair stays equal and the same-currency UI handles it.
      if (
        !action.hasBeneficiary &&
        action.sellCurrency &&
        action.sellCurrency === nextBuyCurrency
      ) {
        const alternative = action.findAlternative?.(action.sellCurrency);
        if (alternative) {
          nextBuyCurrency = alternative;
        }
      }
      if (
        action.sellCurrency !== prev.sellCurrency ||
        nextBuyCurrency !== prev.buyCurrency
      ) {
        return {
          ...state,
          exchange: {
            ...prev,
            sellCurrency: action.sellCurrency,
            buyCurrency: nextBuyCurrency,
          },
        };
      }
      return state;
    }

    case "RESET":
      return {
        ...state,
        exchange: {
          sellCurrency: action.sellCurrency,
          sellAmount: 0,
          buyCurrency: action.buyCurrency,
          buyAmount: 0,
          lastEditedSide: "sell",
          quoteType: QuoteAmountType.ReceiveAmount,
        },
        error: null,
        shouldPollOnSuccess: false,
        areCurrenciesResolved: false,
      };

    case "RESTORE_FROM_SESSION":
      return {
        ...state,
        exchange: { ...state.exchange, ...action.parsed },
      };

    case "SET_ERROR":
      if (state.error === action.message) return state;
      return { ...state, error: action.message };

    case "CLEAR_ERROR":
      if (state.error === null) return state;
      return { ...state, error: null };

    case "SET_SHOULD_POLL":
      if (state.shouldPollOnSuccess === action.value) return state;
      return { ...state, shouldPollOnSuccess: action.value };

    case "SET_CURRENCIES_RESOLVED":
      if (state.areCurrenciesResolved === action.value) return state;
      return { ...state, areCurrenciesResolved: action.value };

    default:
      return state;
  }
}
